import { useState, useEffect } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Edit, Save, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import RichTextEditor from "@/components/portfolio/form/RichTextEditor";

const languages = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
];

export default function PortfolioContent() {
  const [activeLanguage, setActiveLanguage] = useState('tr');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const [contents, setContents] = useState({
    tr: { content: '' },
    en: { content: '' },
    ar: { content: '' }
  });

  useEffect(() => {
    const fetchContents = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('page_contents')
          .select('*')
          .in('page_key', ['portfolio_tr', 'portfolio_en', 'portfolio_ar']);
        
        if (error) throw error;
        
        if (data) {
          const newContents = { ...contents };
          data.forEach(item => {
            const lang = item.page_key.split('_')[1];
            if (lang && newContents[lang]) {
              newContents[lang].content = item.content;
            }
          });
          setContents(newContents);
        }
      } catch (error) {
        console.error('Error fetching contents:', error);
        toast({
          title: "Veri yükleme hatası",
          description: "İçerikler yüklenirken bir hata oluştu.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchContents();
  }, [toast]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const updates = Object.entries(contents).map(([lang, data]) => ({
        page_key: `portfolio_${lang}`,
        content: data.content
      }));

      const { error } = await supabase
        .from('page_contents')
        .upsert(updates, {
          onConflict: 'page_key',
          ignoreDuplicates: false
        });
      
      if (error) throw error;
      
      toast({
        title: "İçerik kaydedildi",
        description: "Portfolyo içeriği başarıyla güncellendi.",
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: "Hata",
        description: "İçerik kaydedilirken bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <ContentLayout 
        title="Portfolyo Yazısı" 
        backLink="/dashboard"
      >
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout 
      title="Portfolyo Yazısı" 
      backLink="/dashboard"
    >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Portfolyo İçeriği</CardTitle>
            <CardDescription>
              Portfolyo sayfasında görüntülenecek metin içeriğini düzenleyin
            </CardDescription>
          </div>
          <Button
            variant={isEditing ? "outline" : "default"}
            onClick={() => setIsEditing(!isEditing)}
            className="ml-auto"
          >
            {isEditing ? "İptal" : (
              <>
                <Edit className="mr-2 h-4 w-4" />
                Düzenle
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeLanguage} onValueChange={setActiveLanguage} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              {languages.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code} className="flex items-center gap-2">
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {languages.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                {isEditing ? (
                  <div className="h-[500px]">
                    <RichTextEditor 
                      content={contents[lang.code].content}
                      setContent={(value) => setContents(prev => ({
                        ...prev,
                        [lang.code]: { ...prev[lang.code], content: value }
                      }))}
                      className="h-full"
                    />
                  </div>
                ) : (
                  <ScrollArea className="h-96 w-full rounded-md border p-4">
                    <div dangerouslySetInnerHTML={{ __html: contents[lang.code].content }} />
                  </ScrollArea>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
        {isEditing && (
          <CardFooter className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              İptal
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Kaydediliyor
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Kaydet
                </>
              )}
            </Button>
          </CardFooter>
        )}
      </Card>
    </ContentLayout>
  );
}
